import React from 'react';
import { Controller } from 'react-hook-form';
import {
  LabeledInput,
  LabeledSelect,
  headerCell,
  valueCell,
  srOnly
} from './Fields';

const FormGrid: React.FC<{
  control: any;
  errors: Record<string, { message?: string }>;
  lesson: any;
  amOptions: Array<{ value: string | number; label: string }>;
  pmOptions: Array<{ value: string | number; label: string }>;
  fdOptions: Array<{ value: string | number; label: string }>;
  hotelAgentOptions: Array<{ value: string | number; label: string }>;
  watchedCc: 'cash' | 'commission';
  gross: number;
  commissionValue: number;
  net: number;
}> = ({
  control,
  errors,
  lesson,
  amOptions,
  pmOptions,
  fdOptions,
  hotelAgentOptions,
  watchedCc,
  gross,
  commissionValue,
  net
}) => {
  return (
    <form className='grid grid-cols-12' role='grid' aria-readonly='true'>
      <div className={`col-span-3 ${headerCell}`}>Instructor</div>
      <div className={`col-span-3 ${headerCell}`}>Client name</div>
      <div className={`col-span-3 ${headerCell}`}>Service</div>
      <div className={`col-span-3 ${headerCell}`}># AM</div>

      <div className={`col-span-3 ${valueCell}`}>
        {lesson?.instructor?.name ?? '-'}
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <Controller
          name='clientName'
          control={control}
          render={({ field }) => (
            <>
              <LabeledInput
                id='client_name'
                label='Client name'
                placeholder='Enter client name'
                value={field.value}
                onChange={field.onChange}
                describedById={
                  errors.clientName ? 'client_name_error' : undefined
                }
                ariaInvalid={Boolean(errors.clientName)}
              />
              {errors.clientName && (
                <p
                  id='client_name_error'
                  className='mt-1 text-xs text-red-600'
                  role='alert'
                >
                  {errors.clientName.message as string}
                </p>
              )}
            </>
          )}
        />
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        {lesson?.lesson_code?.code ?? '-'}
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <Controller
          name='am'
          control={control}
          render={({ field }) => (
            <>
              <LabeledSelect
                id='am'
                label='AM count'
                value={field.value}
                onChange={field.onChange}
                options={amOptions}
                describedById={errors.am ? 'am_error' : undefined}
                ariaInvalid={Boolean(errors.am)}
              />
              {errors.am && (
                <p
                  id='am_error'
                  className='mt-1 text-xs text-red-600'
                  role='alert'
                >
                  {(errors.am.message as string) || 'Invalid AM'}
                </p>
              )}
            </>
          )}
        />
      </div>

      <div className={`col-span-3 ${headerCell}`}># PM</div>
      <div className={`col-span-3 ${headerCell}`}># FD</div>
      <div className={`col-span-3 ${headerCell}`}>Hotel / Agent</div>
      <div className={`col-span-3 ${headerCell}`}>Mountain</div>

      <div className={`col-span-3 ${valueCell}`}>
        <Controller
          name='pm'
          control={control}
          render={({ field }) => (
            <>
              <LabeledSelect
                id='pm'
                label='PM count'
                value={field.value}
                onChange={field.onChange}
                options={pmOptions}
                describedById={errors.pm ? 'pm_error' : undefined}
                ariaInvalid={Boolean(errors.pm)}
              />
              {errors.pm && (
                <p
                  id='pm_error'
                  className='mt-1 text-xs text-red-600'
                  role='alert'
                >
                  {(errors.pm.message as string) || 'Invalid PM'}
                </p>
              )}
            </>
          )}
        />
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <Controller
          name='fd'
          control={control}
          render={({ field }) => (
            <>
              <LabeledSelect
                id='fd'
                label='FD count'
                value={field.value}
                onChange={field.onChange}
                options={fdOptions}
                describedById={errors.fd ? 'fd_error' : undefined}
                ariaInvalid={Boolean(errors.fd)}
              />
              {errors.fd && (
                <p
                  id='fd_error'
                  className='mt-1 text-xs text-red-600'
                  role='alert'
                >
                  {(errors.fd.message as string) || 'Invalid FD'}
                </p>
              )}
            </>
          )}
        />
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <div>
          <label htmlFor='hotel_agent' className={srOnly}>
            Hotel or Agent
          </label>
          <Controller
            name='hotelAgentId'
            control={control}
            render={({ field }) => (
              <>
                <select
                  id='hotel_agent'
                  className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  value={field.value === '' ? '' : Number(field.value)}
                  disabled={watchedCc === 'cash'}
                  aria-disabled={watchedCc === 'cash'}
                  onChange={e =>
                    field.onChange(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  aria-label='Hotel or Agent'
                  aria-invalid={errors.hotelAgentId ? true : undefined}
                  aria-describedby={
                    errors.hotelAgentId ? 'hotel_agent_error' : undefined
                  }
                >
                  <option value=''>Select Hotel / Agent</option>
                  {hotelAgentOptions.map(opt => (
                    <option key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.hotelAgentId && watchedCc === 'commission' && (
                  <p
                    id='hotel_agent_error'
                    className='mt-1 text-xs text-red-600'
                    role='alert'
                  >
                    {(errors.hotelAgentId.message as string) ||
                      'Select a valid Hotel/Agent'}
                  </p>
                )}
              </>
            )}
          />
        </div>
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        {lesson?.mountain?.name ?? 'N/A'}
      </div>

      <div className={`col-span-3 ${headerCell}`}>Time</div>
      <div className={`col-span-3 ${headerCell}`}>CC</div>
      <div className={`col-span-3 ${headerCell}`}>Gross</div>
      <div className={`col-span-3 ${headerCell}`}>Commission</div>

      <div className={`col-span-3 ${valueCell}`}>{lesson?.start ?? ''}</div>
      <div className={`col-span-3 ${valueCell}`}>
        <div>
          <label htmlFor='cc_method' className={srOnly}>
            Payment method
          </label>
          <Controller
            name='cc'
            control={control}
            render={({ field }) => (
              <select
                id='cc_method'
                className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                value={field.value}
                onChange={e =>
                  field.onChange(e.target.value as 'cash' | 'commission')
                }
                aria-label='Payment method'
              >
                <option value='cash'>Cash</option>
                <option value='commission'>Commission</option>
              </select>
            )}
          />
        </div>
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <output aria-live='polite'>{gross}</output>
      </div>
      <div className={`col-span-3 ${valueCell}`}>
        <output aria-live='polite'>{commissionValue}</output>
      </div>

      <div className={`col-span-3 ${headerCell}`}>Level</div>
      <div className={`col-span-3 ${headerCell}`}>Net</div>
      <div className={`col-span-6 ${headerCell}`}></div>

      <div className={`col-span-3 ${valueCell}`}>{lesson?.level ?? '-'}</div>
      <div className={`col-span-3 ${valueCell}`}>
        <output aria-live='polite'>{net}</output>
      </div>
      <div className={`col-span-6 ${valueCell}`}></div>
    </form>
  );
};

export default React.memo(FormGrid);
